import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Camera, Plus } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "sonner";
import logo from '../assets/images/logo-artie.png';

export default function Onboarding1() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuthStore();
  const [fullName, setFullName] = useState(user?.name || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.profile_photo || null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  useEffect(() => {
    if (user) {
      if (!fullName && user.name) setFullName(user.name);
      if (!avatarPreview && user.profile_photo) setAvatarPreview(user.profile_photo);
    }
  }, [user]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!fullName.trim()) {
      toast.error("Please enter your full name");
      return;
    }

    setIsSubmitting(true);
    try {
      let profilePhotoUrl = user?.profile_photo;

      if (avatarFile) {
        // Upload the new profile photo
        const s3Key = await apiClient.uploadProfilePhoto(avatarFile);
        profilePhotoUrl = s3Key;
      }

      await apiClient.updateCurrentUser({
        name: fullName,
        profile_photo: profilePhotoUrl
      });

      await checkAuth();
      toast.success("Profile updated!");
      
      // Proceed to dashboard or next step (for now dashboard as per request implication, 
      // but user said "go to other page", likely dashboard or next onboarding step if it existed)
      // The prompt says "go to other page" implies there might be a next step, but for now just handle this page.
      // And "Update Login Flow... to redirect to /onboarding/1... instead of default dashboard".
      // So presumably after this, we go to dashboard or step 2.
      navigate("/onboarding/2");

    } catch (error) {
      console.error(error);
      toast.error("Failed to update profile");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-hidden flex flex-col items-center justify-center font-ibm">

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 w-full max-w-5xl mx-auto px-6 py-8 flex items-center justify-between z-50">
        <div className="flex items-center gap-3">
          {/* Back Button */}
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-[oklch(1.0000_0_0)] border border-[oklch(0.92_0_0)] flex items-center justify-center text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:border-[oklch(0.2686_0_0)] transition-all"
          >
            <ArrowLeft className="w-5 h-5 stroke-[1.5]" />
          </button>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-end">
            <span className="text-xs font-semibold text-[oklch(0.55_0_0)] font-ibm uppercase tracking-wider">
              Profile
            </span>
            <div className="flex gap-1 mt-1">
              <div className="w-2 h-2 rounded-full bg-[oklch(0.70_0.18_55)]"></div>
              <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
              <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
              <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="w-full max-w-lg px-6 z-10 flex flex-col items-center">
        {/* Artie The Guide */}
        <div className="w-full max-w-lg mb-8 animate-in slide-in-from-bottom-4 duration-700 fade-in">
          <div className="flex items-start gap-5 bg-white p-6 rounded-2xl border border-[oklch(0.92_0_0)] shadow-sm">
             <div className="hidden sm:block shrink-0">
               <div className="w-16 h-16 rounded-full bg-[oklch(0.97_0.01_60)]/30 border-2 border-white shadow-md overflow-hidden">
                  <img src={logo} alt="Artie" className="w-full h-full object-cover" />
               </div>
             </div>
             <div className="space-y-1">
               <h1 className="text-2xl font-bold font-cabinet text-[oklch(0.2686_0_0)]">
                  Hi, I'm Artie! 🐾
               </h1>
               <p className="text-[oklch(0.55_0_0)] font-ibm text-sm leading-relaxed">
                  I'm your creative sidekick. Let's get your profile set up so I know who I'm working with. It'll only take a moment.
               </p>
             </div>
          </div>
        </div>

        {/* Profile Form */}
        <div className="border-[oklch(0.92_0_0)] animate-in slide-in-from-bottom-4 fade-in duration-700 delay-100 bg-white w-full border rounded-3xl pt-8 pr-8 pb-8 pl-8 shadow-sm backdrop-blur-sm">
          {/* Avatar Upload */}
          <div className="flex justify-center mb-8">
            <div className="relative cursor-pointer group">
              <input 
                type="file" 
                className="hidden" 
                accept="image/*"
                onChange={handleFileChange}
                id="avatar-upload"
              />
              <label htmlFor="avatar-upload" className="cursor-pointer block">
                {/* Avatar Circle */}
                <div className="w-24 h-24 rounded-full bg-[oklch(0.97_0.01_60)] border-2 border-dashed border-[oklch(0.92_0_0)] flex items-center justify-center overflow-hidden transition-colors group-hover:border-[oklch(0.70_0.18_55)] group-hover:bg-[oklch(1.0000_0_0)] relative">
                  
                  {avatarPreview ? (
                     <img src={avatarPreview} className="w-full h-full object-cover" alt="Preview" />
                  ) : (
                     <Camera className="w-8 h-8 text-[oklch(0.55_0_0)] group-hover:text-[oklch(0.70_0.18_55)] transition-colors stroke-[1.5]" />
                  )}
                </div>

                {/* Plus Badge */}
                <div className="absolute bottom-0 right-0 w-8 h-8 bg-[oklch(0.70_0.18_55)] rounded-full flex items-center justify-center text-white shadow-md border-2 border-[oklch(1.0000_0_0)] transition-transform group-hover:scale-110">
                  <Plus className="w-4 h-4 stroke-[3]" />
                </div>
              </label>
            </div>
            <div className="absolute mt-28 text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <span className="text-xs font-medium text-[oklch(0.55_0_0)]">
                Upload photo
              </span>
            </div>
          </div>

          {/* Inputs */}
          <div className="space-y-6">
            {/* Full Name */}
            <div className="relative group">
              <div className="">
                <input
                  type="text"
                  id="fullname"
                  placeholder=" "
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="block w-full pb-3 text-xl font-medium font-cabinet text-[oklch(0.2686_0_0)] placeholder-transparent bg-transparent border-b-2 border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:outline-none transition-colors"
                />
                <label 
                  htmlFor="fullname" 
                  className={`absolute left-0 top-full mt-2 text-sm font-ibm transition-all duration-200 
                    ${fullName ? 'opacity-100 translate-y-0' : 'peer-placeholder-shown:opacity-0 peer-placeholder-shown:translate-y-2 opacity-100 translate-y-0'}
                    text-[oklch(0.55_0_0)] group-focus-within:text-[oklch(0.70_0.18_55)]`}
                >
                  Full Name
                </label>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer Action */}
      <footer className="fixed bottom-0 left-0 right-0 p-6 z-50 flex justify-center animate-in slide-in-from-bottom-4 fade-in duration-700 delay-200">
        <div className="w-full max-w-lg flex items-center justify-end">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="group flex items-center gap-2 bg-[oklch(0.2686_0_0)] hover:bg-[oklch(0.2686_0_0)]/90 text-[oklch(1.0000_0_0)] pl-6 pr-5 py-3 rounded-full text-sm font-medium transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5 font-ibm disabled:opacity-70 disabled:hover:translate-y-0"
          >
            <span>{isSubmitting ? "Saving..." : "Continue"}</span>
            <div className="bg-[oklch(1.0000_0_0)]/20 rounded-full p-1 group-hover:translate-x-1 transition-transform">
              <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>
        </div>
      </footer>
    </div>
  );
}